// pages/eventos/admin/PainelTodosSorteiosPage.jsx

import React, { useEffect, useMemo, useState } from "react";
import axios from "../../../axiosConfig";
import * as XLSX from "xlsx";
import {
  CheckCircle2,
  Clock,
  FileSpreadsheet,
  Gift,
  Search,
  ThumbsUp,
  Trophy,
} from "lucide-react";
import "./PainelTodosSorteiosPage.css";

function PainelTodosSorteiosPage() {
  const [sorteios, setSorteios] = useState([]);
  const [filtro, setFiltro] = useState("");
  const [erro, setErro] = useState("");
  const [loading, setLoading] = useState(true);
  const [paginaAtual, setPaginaAtual] = useState(1);
  const itensPorPagina = 10;
  const [loadingAprovandoId, setLoadingAprovandoId] = useState(null);

  useEffect(() => {
    async function carregarSorteios() {
      setLoading(true);

      try {
        const res = await axios.get("/admin/sorteios");
        setSorteios(Array.isArray(res.data) ? res.data : []);
        setErro("");
      } catch (err) {
        console.error("Erro ao buscar sorteios:", err);
        setErro("Erro ao carregar dados dos sorteios.");
      } finally {
        setLoading(false);
      }
    }

    carregarSorteios();
  }, []);

  const aprovarPremio = async (sorteio) => {
    setLoadingAprovandoId(sorteio.id);

    try {
      const res = await axios.post("/admin/aprovar-premio", {
        usuario_id: sorteio.usuario_id,
        evento_id: sorteio.evento_id,
        evento_slug: sorteio.evento_slug,
        premio: sorteio.premio,
      });

      const atualizado = sorteios.map((s) =>
        s.id === sorteio.id
          ? {
              ...s,
              status_evento:
                res.data.tipo === "pontos"
                  ? "pontos_creditados"
                  : "resgate_criado",
            }
          : s
      );

      setSorteios(atualizado);
    } catch (err) {
      console.error("Erro ao aprovar prêmio:", err);
      alert("Erro ao aprovar prêmio.");
    } finally {
      setLoadingAprovandoId(null);
    }
  };

  const exportarExcel = () => {
    const planilha = sorteios.map((s) => ({
      Nome: s.nome_usuario,
      Email: s.email,
      Telefone: s.telefone,
      Empresa: s.nome_empresa || "-",
      Evento: s.evento_slug,
      Prêmio: s.premio,
      Status: s.status_evento?.replaceAll("_", " "),
      Data: new Date(s.data_participacao).toLocaleString("pt-BR"),
    }));

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(planilha);

    XLSX.utils.book_append_sheet(wb, ws, "Sorteios");
    XLSX.writeFile(wb, `sorteios-todos-${Date.now()}.xlsx`);
  };

  const sorteiosFiltrados = useMemo(() => {
    const termo = filtro.toLowerCase();

    return sorteios.filter(
      (s) =>
        s.nome_usuario?.toLowerCase().includes(termo) ||
        s.email?.toLowerCase().includes(termo) ||
        s.evento_slug?.toLowerCase().includes(termo)
    );
  }, [sorteios, filtro]);

  const totalPaginas = Math.max(
    1,
    Math.ceil(sorteiosFiltrados.length / itensPorPagina)
  );

  const sorteiosPagina = sorteiosFiltrados.slice(
    (paginaAtual - 1) * itensPorPagina,
    paginaAtual * itensPorPagina
  );

  const resumo = useMemo(() => {
    return {
      total: sorteios.length,
      emAnalise: sorteios.filter((s) =>
        ["em_analise", "aguardando_aprovacao"].includes(s.status_evento)
      ).length,
      pontosCreditados: sorteios.filter(
        (s) => s.status_evento === "pontos_creditados"
      ).length,
      resgatesCriados: sorteios.filter(
        (s) => s.status_evento === "resgate_criado"
      ).length,
    };
  }, [sorteios]);

  const formatarNumero = (valor) =>
    Number(valor || 0).toLocaleString("pt-BR", {
      maximumFractionDigits: 0,
    });

  const formatarData = (data) => {
    if (!data) return "—";

    try {
      return new Date(data).toLocaleString("pt-BR");
    } catch {
      return "—";
    }
  };

  const getStatusLabel = (status) =>
    status ? status.replaceAll("_", " ") : "sem status";

  return (
    <div className="sorteiosAdmin-page">
      <div className="sorteiosAdmin-topbar">
        <div>
          <span className="sorteiosAdmin-kicker">Eventos promocionais</span>

          <h2>Sorteados nos Eventos</h2>

          <p>
            Acompanhe todos os participantes sorteados, aprove prêmios pendentes
            e exporte a listagem consolidada dos eventos.
          </p>
        </div>

        <button
          type="button"
          onClick={exportarExcel}
          className="sorteiosAdmin-btn primary"
          aria-label="Exportar sorteios em Excel"
        >
          <FileSpreadsheet size={16} />
          Exportar Excel
        </button>
      </div>

      {erro && <div className="sorteiosAdmin-alert danger">{erro}</div>}

      <div className="sorteiosAdmin-cards">
        <div className="sorteiosAdmin-card">
          <span>Total de sorteios</span>
          <strong>{formatarNumero(resumo.total)}</strong>
        </div>

        <div className="sorteiosAdmin-card warning">
          <span>Em análise</span>
          <strong>{formatarNumero(resumo.emAnalise)}</strong>
        </div>

        <div className="sorteiosAdmin-card success">
          <span>Pontos creditados</span>
          <strong>{formatarNumero(resumo.pontosCreditados)}</strong>
        </div>

        <div className="sorteiosAdmin-card info">
          <span>Resgates criados</span>
          <strong>{formatarNumero(resumo.resgatesCriados)}</strong>
        </div>
      </div>

      <div className="sorteiosAdmin-filtrosCard">
        <div className="sorteiosAdmin-searchInput">
          <Search size={15} />
          <input
            type="text"
            placeholder="Buscar por nome, e-mail ou evento"
            value={filtro}
            onChange={(e) => {
              setFiltro(e.target.value);
              setPaginaAtual(1);
            }}
          />
        </div>

        <span>{formatarNumero(sorteiosFiltrados.length)} resultado(s)</span>
      </div>

      <div className="sorteiosAdmin-tableCard">
        <div className="sorteiosAdmin-tableHeader">
          <div>
            <h3>Lista de sorteios</h3>
            <span>Prêmios pendentes podem ser aprovados diretamente na tabela.</span>
          </div>

          <Trophy size={18} />
        </div>

        {loading ? (
          <div className="sorteiosAdmin-loading">Carregando sorteios...</div>
        ) : (
          <>
            <div className="sorteiosAdmin-tableWrapper">
              <table className="sorteiosAdmin-table">
                <thead>
                  <tr>
                    <th>Cliente</th>
                    <th>Contato</th>
                    <th>Empresa</th>
                    <th>Prêmio / Evento</th>
                    <th>Data</th>
                    <th>Status</th>
                    <th>Ações</th>
                  </tr>
                </thead>

                <tbody>
                  {sorteiosPagina.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="sorteiosAdmin-empty">
                        Nenhum sorteio encontrado.
                      </td>
                    </tr>
                  ) : (
                    sorteiosPagina.map((s) => (
                      <tr key={s.id}>
                        <td>
                          <div className="sorteiosAdmin-cellMain">
                            <strong>{s.nome_usuario || "—"}</strong>
                            <span>ID usuário #{s.usuario_id || "—"}</span>
                          </div>
                        </td>

                        <td>
                          <div className="sorteiosAdmin-cellMain">
                            <span>{s.email || "—"}</span>
                            <span>{s.telefone || "—"}</span>
                          </div>
                        </td>

                        <td>
                          <div className="sorteiosAdmin-cellMain">
                            <strong>{s.nome_empresa || "—"}</strong>
                          </div>
                        </td>

                        <td>
                          <div className="sorteiosAdmin-cellMain">
                            <span className="sorteiosAdmin-premio">
                              <Gift size={13} />
                              {s.premio || "—"}
                            </span>
                            <span>{s.evento_slug || "—"}</span>
                          </div>
                        </td>

                        <td>
                          <div className="sorteiosAdmin-cellMain">
                            <span>{formatarData(s.data_participacao)}</span>
                          </div>
                        </td>

                        <td>
                          <span
                            className={`sorteiosAdmin-status status-${s.status_evento}`}
                          >
                            {getStatusLabel(s.status_evento)}
                          </span>
                        </td>

                        <td>
                          {["em_analise", "aguardando_aprovacao"].includes(
                            s.status_evento
                          ) ? (
                            <button
                              type="button"
                              className="sorteiosAdmin-actionBtn approve"
                              disabled={loadingAprovandoId === s.id}
                              onClick={() => aprovarPremio(s)}
                            >
                              {loadingAprovandoId === s.id ? (
                                <>
                                  <Clock size={14} />
                                  Aprovando...
                                </>
                              ) : (
                                <>
                                  <ThumbsUp size={14} />
                                  Aprovar
                                </>
                              )}
                            </button>
                          ) : (
                            <span className="sorteiosAdmin-approved">
                              <CheckCircle2 size={14} />
                              Processado
                            </span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {totalPaginas > 1 && (
              <div className="sorteiosAdmin-paginacao">
                <button
                  type="button"
                  onClick={() => setPaginaAtual((prev) => Math.max(prev - 1, 1))}
                  disabled={paginaAtual === 1}
                >
                  Anterior
                </button>

                <span>
                  Página {paginaAtual} de {totalPaginas}
                </span>

                <button
                  type="button"
                  onClick={() =>
                    setPaginaAtual((prev) => Math.min(prev + 1, totalPaginas))
                  }
                  disabled={paginaAtual === totalPaginas}
                >
                  Próxima
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default PainelTodosSorteiosPage;